import { DocumentType } from '@doss/prisma/types'
import _ from 'lodash'
import { lookup } from 'mime-types'

export const fileTypeToExtension: Record<DocumentType, string> = {
  [DocumentType.AI]: 'ai',
  [DocumentType.CSV]: 'csv',
  [DocumentType.DOC]: 'doc',
  [DocumentType.DOCX]: 'docx',
  [DocumentType.DWG]: 'dwg',
  [DocumentType.GIF]: 'gif',
  [DocumentType.HTML]: 'html',
  [DocumentType.JPEG]: 'jpeg',
  [DocumentType.JPG]: 'jpg',
  [DocumentType.PDF]: 'pdf',
  [DocumentType.PNG]: 'png',
  [DocumentType.PPT]: 'ppt',
  [DocumentType.PPTX]: 'pptx',
  [DocumentType.SIF]: 'sif',
  [DocumentType.STEP]: 'step',
  [DocumentType.SVG]: 'svg',
  [DocumentType.THREEDM]: '3dm',
  [DocumentType.TXT]: 'txt',
  [DocumentType.WEBP]: 'webp',
  [DocumentType.XLS]: 'xls',
  [DocumentType.XLSM]: 'xlsm',
  [DocumentType.XLSX]: 'xlsx',
  [DocumentType.ZIP]: 'zip',
  [DocumentType.ZPL]: 'zpl',
}

export const fileTypeToMimeType: Record<DocumentType, string> = {
  [DocumentType.AI]: 'application/pdf',
  [DocumentType.CSV]: 'text/csv',
  [DocumentType.DOC]: 'application/msword',
  [DocumentType.DOCX]: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  [DocumentType.DWG]: 'application/acad',
  [DocumentType.GIF]: 'image/gif',
  [DocumentType.HTML]: 'text/html',
  [DocumentType.JPEG]: 'image/jpeg',
  [DocumentType.JPG]: 'image/jpeg',
  [DocumentType.PDF]: 'application/pdf',
  [DocumentType.PNG]: 'image/png',
  [DocumentType.PPT]: 'application/vnd.ms-powerpoint',
  [DocumentType.PPTX]: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  [DocumentType.SIF]: 'application/sif',
  [DocumentType.STEP]: 'application/step',
  [DocumentType.SVG]: 'image/svg+xml',
  [DocumentType.THREEDM]: 'model/vnd.3dm',
  [DocumentType.TXT]: 'text/plain',
  [DocumentType.WEBP]: 'image/webp',
  [DocumentType.XLS]: 'application/vnd.ms-excel',
  [DocumentType.XLSX]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  [DocumentType.ZIP]: 'application/zip',
  [DocumentType.XLSM]: 'application/vnd.ms-excel.sheet.macroEnabled.12',
  [DocumentType.ZPL]: 'application/zpl',
}

export const imageTypes = [
  DocumentType.GIF,
  DocumentType.JPEG,
  DocumentType.JPG,
  DocumentType.PNG,
  DocumentType.SVG,
  DocumentType.WEBP,
] as const

export type ImageType = (typeof imageTypes)[number]

/**
 * Gets the MIME type of a file given its extension
 *
 * If the mimetype is found using the mime-type library, return that as its the most accurate. Else
 * use our custom built object for non-normal extensions.
 */
export function getMimeTypeFromExtension(extension: string): string {
  const found = lookup(extension)
  if (found === false) {
    const fileType = _.invert(fileTypeToExtension)[extension]
    const mimeType = fileTypeToMimeType[fileType as DocumentType]
    if (mimeType) {
      return mimeType
    }
    throw new Error(`No MIME type found for ${extension}`)
  }
  return found
}

/**
 * Returns whether the document type is an image type
 */
export function isImageTypeDocument(type: DocumentType): type is ImageType {
  return _.includes(imageTypes, type)
}

/**
 * Returns whether a file type can be previewed on web
 */
export function isPreviewableType(type: DocumentType): boolean {
  switch (type) {
    case DocumentType.AI:
    case DocumentType.STEP:
    case DocumentType.THREEDM:
    case DocumentType.XLSM:
    case DocumentType.DWG:
    case DocumentType.HTML:
    case DocumentType.SIF:
    case DocumentType.ZIP:
    case DocumentType.ZPL:
      return false
    case DocumentType.CSV:
    case DocumentType.DOC:
    case DocumentType.DOCX:
    case DocumentType.GIF:
    case DocumentType.JPEG:
    case DocumentType.JPG:
    case DocumentType.PDF:
    case DocumentType.PNG:
    case DocumentType.PPT:
    case DocumentType.PPTX:
    case DocumentType.SVG:
    case DocumentType.TXT:
    case DocumentType.WEBP:
    case DocumentType.XLS:
    case DocumentType.XLSX:
      return true
  }
}
